import {observer} from "mobx-react";
import {useContext, useEffect} from "react";
import {Button, Skeleton} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {Frame} from "../components/common/Frame";
import {Header} from "../components/sections/Header";
import {AppRoutes} from "../AppRoutes";
import {useNavigate, useParams} from "react-router";
import {AppContext} from "../stores/AppStore";
import useInstance from "../hooks/useInstance";
import {LessonEditorStore, LessonEditorStoreContext} from "../stores/LessonEditorStore";
import {LessonEditorHeader} from "../components/lessons/editor/LessonEditorHeader";
import {LessonEditorContent} from "../components/lessons/editor/LessonEditorContent";

export const LessonEditorPage = observer(() => {
  const {id} = useParams<{id?: string}>();
  const app = useContext(AppContext)!;
  const navigate = useNavigate();
  const store = useInstance(() => new LessonEditorStore(app, id ?? undefined), [app, id]);

  // Redirect if user doesn't have lesson_editor permission or the lesson is locked
  const locked = !!store?.lesson?.locked;
  useEffect(() => {
    if ((app.auth.user && !app.auth.isLessonEditor) || locked) {
      navigate(id ? AppRoutes.lessonPath(id) : AppRoutes.lessons, {replace: true});
    }
  }, [app.auth.user, app.auth.isLessonEditor, locked, id, navigate]);

  if (!id || !store) return null;
  if (!app.auth.isLessonEditor) return null;

  const lessonLabel = store?.lesson?.title ?? "...";

  const content = (
    <Frame direction="column" grow>
      <Header
        pathCrumbs={[
          [AppRoutes.index, "Home"],
          [AppRoutes.lessons, "Lessons"],
          [AppRoutes.lessonPath(id), lessonLabel],
          [undefined, "Edit"],
        ]}
        action={
          <Button
            variant="outlined"
            startIcon={<VisibilityIcon/>}
            onClick={() => navigate(AppRoutes.lessonPath(id))}
          >
            View
          </Button>
        }
      />

      <Frame
        direction="column"
        gap={2}
        padding={2}
        contentWidth
        grow
      >
        {store?.loading ? (
          <LessonEditorPageSkeleton />
        ) : store?.lesson ? (
          <>
            <LessonEditorHeader />
            <LessonEditorContent />
          </>
        ) : null}
      </Frame>
    </Frame>
  );

  return (
    <LessonEditorStoreContext.Provider value={store}>
      {content}
    </LessonEditorStoreContext.Provider>
  );
});

const LessonEditorPageSkeleton = () => {
  return (
    <Frame direction="column" gap={2}>
      {/* Header skeleton */}
      <Frame appearance="card" padding={3} direction="column" gap={1.5} radius={1.5}>
        <Skeleton variant="text" width="60%" height={40} />
        <Skeleton variant="rectangular" width="100%" height={80} />
      </Frame>

      {/* Content skeleton */}
      <Frame direction="column" gap={2}>
        <Frame appearance="card" padding={3} direction="column" gap={3} radius={1.5}>
          <Skeleton variant="text" width="50%" height={32} />
          <Skeleton variant="rectangular" width="100%" height={100} />
          <Skeleton variant="rectangular" width="100%" height={150} />
        </Frame>
      </Frame>
    </Frame>
  );
};
